<template>
  <div class="healthcare-shell">
    <section class="healthcare-hero">
      <div class="hero-content">
        <div class="hero-mark" aria-hidden="true"><v-icon>mdi-shield-check-outline</v-icon></div>
        <div class="hero-copy">
          <div class="page-eyebrow">Mitgliedschaft &amp; Versicherung</div>
          <h1>Healthcare-Bestätigungen</h1>
          <p>Anfragen prüfen, Zahlungen zuordnen und Krankenkassenbestätigungen sicher freischalten.</p>
        </div>
      </div>
      <span class="status-pill" :class="selectedStatus === 'pending' ? 'status-pending' : 'status-accepted'">
        <span class="status-dot"></span>
        {{ selectedStatus === "pending" ? "Offene Anfragen" : "Abgeschlossen" }}
      </span>
    </section>

    <section class="kpi-grid" aria-label="Übersicht der Healthcare-Anfragen">
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-neutral"><v-icon>mdi-format-list-bulleted</v-icon></div>
        <div class="kpi-copy">
          <span>Sichtbare Anfragen</span>
          <strong>{{ loading ? "–" : requestsFiltered.length }}</strong>
          <small>mit aktuellem Filter</small>
        </div>
      </v-card>
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-pending"><v-icon>mdi-clock-outline</v-icon></div>
        <div class="kpi-copy">
          <span>Aktueller Filter</span>
          <strong class="kpi-status">{{ selectedStatus === "pending" ? "Offen" : "Abgeschlossen" }}</strong>
          <small>Status der Ansicht</small>
        </div>
      </v-card>
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-success"><v-icon>mdi-check-decagram-outline</v-icon></div>
        <div class="kpi-copy">
          <span>Ausgewählte Studios</span>
          <strong>{{ selectedStudios.length }}</strong>
          <small>im aktuellen Filter</small>
        </div>
      </v-card>
      <v-card dark flat class="kpi-card">
        <div class="kpi-icon kpi-icon-neutral"><v-icon>mdi-domain</v-icon></div>
        <div class="kpi-copy">
          <span>Studios mit offenen Anfragen</span>
          <strong>{{ loading ? "–" : groupedOpenStudios.length }}</strong>
          <small>studioübergreifend</small>
        </div>
      </v-card>
    </section>

    <v-card dark flat class="filter-surface">
      <div class="surface-header">
        <div>
          <div class="surface-eyebrow">Ansicht anpassen</div>
          <div class="surface-title-row">
            <h2>Filter &amp; Suche</h2>
            <span class="result-count">{{ loading ? "Wird geladen …" : requestsFiltered.length + " Ergebnisse" }}</span>
          </div>
        </div>
      </div>
      <v-row dense class="filter-fields">
        <v-col cols="12" md="5">
          <v-select
            v-model="selectedStudios"
            :loading="loading"
            :disabled="loading"
            :items="availableStudios"
            label="Studios"
            prepend-inner-icon="mdi-map-marker-multiple-outline"
            multiple
            deletable-chips
            small-chips
            outlined
            dense
            dark
            hide-details
            class="modern-field studio-select"
            @change="fetchHealthcareRequests">
            <template v-slot:prepend-item>
              <v-list-item
                class="select-all-item"
                ripple
                role="checkbox"
                tabindex="0"
                :aria-checked="allStudiosSelected ? 'true' : someStudiosSelected ? 'mixed' : 'false'"
                @click="toggleSelectAll"
                @keydown.enter.prevent="toggleSelectAll"
                @keydown.space.prevent="toggleSelectAll">
                <v-list-item-action>
                  <v-icon :color="selectedStudios.length > 0 ? '#58d388' : ''">{{ selectAllIcon }}</v-icon>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title>Alle auswählen / abwählen</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-divider class="select-divider"></v-divider>
            </template>
          </v-select>
        </v-col>
        <v-col cols="12" sm="5" md="3">
          <v-select
            v-model="selectedStatus"
            :loading="loading"
            :disabled="loading"
            :items="[
              { text: 'Offen', value: 'pending' },
              { text: 'Abgeschlossen', value: 'accepted' },
            ]"
            label="Status"
            prepend-inner-icon="mdi-progress-check"
            outlined
            dense
            dark
            hide-details
            class="modern-field"
            @change="fetchHealthcareRequests"></v-select>
        </v-col>
        <v-col cols="12" sm="7" md="4">
          <v-text-field
            v-model="searchFilter"
            label="Vorname, Nachname oder E-Mail"
            prepend-inner-icon="mdi-magnify"
            clearable
            outlined
            dense
            dark
            hide-details
            class="modern-field"></v-text-field>
        </v-col>
      </v-row>
    </v-card>

    <v-card dark flat class="requests-surface">
      <div class="requests-header">
        <div>
          <div class="surface-eyebrow">Aktuelle Auswahl</div>
          <h2>Anfragen</h2>
        </div>
        <div class="status-legend" aria-label="Statuslegende">
          <span><i class="legend-dot legend-pending"></i>Offen</span>
          <span><i class="legend-dot legend-accepted"></i>Abgeschlossen</span>
        </div>
      </div>

      <div v-if="loading" class="loading-state" role="status" aria-live="polite">
        <v-progress-circular color="primary" indeterminate size="30" width="3"></v-progress-circular>
        <strong>Anfragen werden geladen</strong>
        <span>Die aktuelle Studioauswahl wird abgeglichen …</span>
      </div>

      <div v-else-if="requestsFiltered.length === 0" class="empty-state">
        <span class="empty-state-icon"><v-icon>mdi-file-search-outline</v-icon></span>
        <strong>Keine Anfragen gefunden</strong>
        <p>Passe Studios, Status oder Suchbegriff an.</p>
        <div v-if="groupedOpenStudios.length" class="open-studios">
          <div class="open-studios-heading">Studios mit offenen Anfragen</div>
          <div class="open-studio-list">
            <span v-for="(studio, index) in groupedOpenStudios" :key="index" class="open-studio-pill">
              {{ studio.name || "Unbekanntes Studio" }}
              <strong>{{ studio.count }}</strong>
            </span>
          </div>
        </div>
      </div>

      <div v-else class="request-grid">
        <article v-for="requestItem in requestsFiltered" :key="requestItem._id" class="request-card">
          <div class="request-topline">
            <span class="studio-label">
              <v-icon x-small>mdi-map-marker-outline</v-icon>
              {{ (requestItem.studio && requestItem.studio.name) || "Kein Studio" }}
            </span>
            <span class="status-pill" :class="requestItem.status === 'accepted' ? 'status-accepted' : 'status-pending'">
              <span class="status-dot"></span>
              {{ requestItem.status === "accepted" ? "Abgeschlossen" : "Offen" }}
            </span>
          </div>

          <div class="request-person">
            <span class="person-avatar">
              {{ requestItem.user && requestItem.user.vorname ? requestItem.user.vorname.charAt(0) : "?"
              }}{{ requestItem.user && requestItem.user.nachname ? requestItem.user.nachname.charAt(0) : "" }}
            </span>
            <div>
              <strong>{{ requestUserName(requestItem) }}</strong>
              <span>{{ (requestItem.user && requestItem.user.email) || "Keine E-Mail" }}</span>
            </div>
          </div>

          <div class="contract-box">
            <div class="contract-heading">
              <v-icon small>mdi-card-account-details-outline</v-icon>
              <span>Vertragstyp</span>
            </div>
            <strong>{{ (requestItem.contract && requestItem.contract.title) || "Ohne Bezeichnung" }}</strong>
            <div class="contract-dates">
              <span><v-icon x-small>mdi-calendar-start</v-icon>{{ (requestItem.contract && requestItem.contract.start) || "–" }}</span>
              <v-icon x-small>mdi-arrow-right</v-icon>
              <span><v-icon x-small>mdi-calendar-end</v-icon>{{ (requestItem.contract && requestItem.contract.end) || "–" }}</span>
            </div>
          </div>

          <div class="request-footer">
            <span class="created-date">
              <v-icon x-small>mdi-calendar-blank-outline</v-icon>
              {{ requestItem.createdAt ? $moment(requestItem.createdAt).format("DD.MM.YYYY") : "–" }}
            </span>
            <div v-if="requestItem.status === 'pending'" class="request-actions">
              <v-btn
                outlined
                class="unlock-action"
                :aria-label="`Healthcare-Anfrage von ${requestUserName(requestItem)} freischalten`"
                @click="setNewSelectedRequest(requestItem)">
                <v-icon left small>mdi-lock-open-outline</v-icon>
                Freischalten
              </v-btn>
              <v-btn
                icon
                class="touch-action delete-action"
                :aria-label="`Healthcare-Anfrage von ${requestUserName(requestItem)} löschen`"
                @click="deleteHealthcareRequest(requestItem._id)">
                <v-icon small>mdi-delete-outline</v-icon>
              </v-btn>
            </div>
            <v-btn
              v-else
              class="primary-action download-action"
              depressed
              :aria-label="`Healthcare-Bestätigung von ${requestUserName(requestItem)} als PDF herunterladen`"
              @click="createPDF(requestItem)">
              <v-icon left small>mdi-download-outline</v-icon>
              PDF laden
            </v-btn>
          </div>
        </article>
      </div>
    </v-card>

    <v-dialog
      :value="selectedRequest !== null"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="720"
      persistent
      scrollable
      content-class="healthcare-confirmation-dialog"
      overlay-color="#111112"
      :overlay-opacity="0.78">
      <v-card v-if="selectedRequest && selectedRequest.contract" dark class="confirmation-dialog">
        <v-card-title class="dialog-header">
          <div class="dialog-mark" aria-hidden="true"><v-icon>mdi-lock-open-outline</v-icon></div>
          <div class="dialog-title-copy">
            <div class="dialog-eyebrow">Anfrage freischalten</div>
            <h2>{{ requestUserName(selectedRequest) }}</h2>
            <p>{{ (selectedRequest.user && selectedRequest.user.email) || "Keine E-Mail hinterlegt" }}</p>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon class="touch-action dialog-close" aria-label="Dialog schliessen" @click="selectedRequest = null">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider class="dialog-divider"></v-divider>

        <v-card-text class="dialog-scroll">
          <section class="dialog-section customer-section">
            <div class="section-heading">
              <span class="section-icon"><v-icon small>mdi-account-outline</v-icon></span>
              <div>
                <span>Kundendaten</span>
                <h3>Person &amp; Studio</h3>
              </div>
            </div>
            <dl class="detail-list">
              <div class="detail-row">
                <dt>Studio</dt>
                <dd>{{ (selectedRequest.studio && selectedRequest.studio.name) || "–" }}</dd>
              </div>
              <div class="detail-row">
                <dt>Vorname</dt>
                <dd>{{ (selectedRequest.user && selectedRequest.user.vorname) || "–" }}</dd>
              </div>
              <div class="detail-row">
                <dt>Nachname</dt>
                <dd>{{ (selectedRequest.user && selectedRequest.user.nachname) || "–" }}</dd>
              </div>
              <div class="detail-row">
                <dt>E-Mail</dt>
                <dd>{{ (selectedRequest.user && selectedRequest.user.email) || "–" }}</dd>
              </div>
            </dl>
          </section>

          <section class="dialog-section payments-section">
            <div class="section-heading section-heading-between">
              <div class="section-heading-copy">
                <span class="section-icon"><v-icon small>mdi-cash-multiple</v-icon></span>
                <div>
                  <span>Zahlungen</span>
                  <h3>Gefundene Transaktionen</h3>
                </div>
              </div>
              <span class="result-count">{{ contractTransactions.length }}</span>
            </div>

            <div v-if="contractTransactions.length" class="transaction-list">
              <label v-for="(trans, index) in contractTransactions" :key="index" class="transaction-row">
                <span class="transaction-icon"><v-icon>mdi-credit-card-outline</v-icon></span>
                <span class="transaction-copy">
                  <strong>{{ trans.Description || "Transaktion" }}</strong>
                  <span :class="{ 'is-negative': trans.Value && trans.Value.value < 0 }">
                    {{ trans.Value ? trans.Value.value + " " + trans.Value.currencyCode : "–" }}
                    <em>{{ additionalPaymentInfos(trans) }}</em>
                  </span>
                </span>
                <v-checkbox v-model="trans.checked" color="primary" hide-details class="transaction-check"></v-checkbox>
              </label>
            </div>
            <div v-else class="inline-empty">
              <v-icon small>mdi-cash-remove</v-icon>
              Keine passenden Zahlungen gefunden.
            </div>

            <v-btn v-if="contractTransactions.length" outlined class="take-over-action" @click="insertCost">
              Auswahl übernehmen
              <v-icon right small>mdi-arrow-down</v-icon>
            </v-btn>
          </section>

          <section class="dialog-section contract-section">
            <div class="section-heading">
              <span class="section-icon"><v-icon small>mdi-file-document-edit-outline</v-icon></span>
              <div>
                <span>Bestätigung</span>
                <h3>Vertragsangaben</h3>
              </div>
            </div>
            <v-text-field
              v-model="selectedRequest.amount"
              type="number"
              label="Kosten des Abos (KK-Bestätigung)"
              prepend-inner-icon="mdi-currency-chf"
              outlined
              dense
              dark
              hide-details
              class="dialog-field"></v-text-field>
            <v-text-field
              v-model="selectedRequest.contract.title"
              label="Abo-Titel"
              prepend-inner-icon="mdi-card-text-outline"
              outlined
              dense
              dark
              hide-details
              class="dialog-field"></v-text-field>
            <div class="wellness-option">
              <v-checkbox v-model="selectedRequest.wellness" color="primary" hide-details label="Wellness und/oder Bad inkludiert"></v-checkbox>
            </div>
          </section>
        </v-card-text>

        <v-divider class="dialog-divider"></v-divider>
        <v-card-actions class="dialog-actions">
          <v-btn text class="secondary-action" @click="selectedRequest = null">Abbrechen</v-btn>
          <v-spacer></v-spacer>
          <v-btn outlined class="test-pdf-action" @click="createPDF(selectedRequest)">
            <v-icon left small>mdi-file-pdf-box</v-icon>
            Test-PDF
          </v-btn>
          <v-btn class="primary-action" depressed @click="updateContractPayment">
            <v-icon left small>mdi-lock-open-outline</v-icon>
            Freischalten
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import api from "@/api"
import { mapState } from "vuex"
import userService from "@/services/userService"
export default {
  data() {
    return {
      userService,
      api,
      searchFilter: "",
      selectedStatus: "pending",
      loading: true,
      requests: [], // Hält die Array-Response vom Server
      selectedStudios: [], // Vom Nutzer gewählte Studios (IDs oder ggf. Namen)
      selectedRequest: null, // Aktuell ausgewähltes Objekt für den Dialog
      groupedOpenStudios: [], // Studios mit offenen Anfragen
    }
  },
  created() {
    const currentStudio = this.userService.getStudio()
    if (currentStudio && currentStudio._id) this.selectedStudios.push(currentStudio._id)
    this.fetchHealthcareRequests()
  },
  methods: {
    requestUserName(requestItem) {
      const user = requestItem && requestItem.user ? requestItem.user : {}
      return [user.vorname, user.nachname].filter(Boolean).join(" ") || "Unbekannte Person"
    },
    async deleteHealthcareRequest(requestId) {
      if (confirm("Möchtest du diese Anfrage wirklich löschen?")) {
        api
          .deleteHealthcareRequest(requestId)
          .then(() => {
            this.$toast.success("Anfrage gelöscht")
            this.fetchHealthcareRequests()
          })
          .catch((e) => {
            this.$toast.error("Fehler beim Löschen der Anfrage")
          })
      }
    },
    setNewSelectedRequest(requestItem) {
      if (!requestItem || !requestItem.contract) {
        this.$toast.error("Vertragsdaten konnten nicht geladen werden")
        return
      }
      const transactions = Array.isArray(requestItem.contract.transactions)
        ? requestItem.contract.transactions.map((transaction) => ({
            ...transaction,
            det: transaction.det
              ? {
                  ...transaction.det,
                  AccountTransactionDetail: Array.isArray(transaction.det.AccountTransactionDetail)
                    ? transaction.det.AccountTransactionDetail.map((detail) => ({ ...detail }))
                    : [],
                }
              : transaction.det,
          }))
        : requestItem.contract.transactions
      const contract = { ...requestItem.contract, transactions }
      let amount = 0
      if ((contract.title || "").includes("Mitarbeiter")) {
        contract.title = "PREMIUM PLUS"
        amount = 1449
      }

      this.selectedRequest = { ...requestItem, contract, amount }
    },
    toggleSelectAll() {
      if (this.allStudiosSelected) {
        // Wenn alle ausgewählt sind -> Auswahl aufheben
        this.selectedStudios = []
      } else {
        // Sonst -> Alle auswählen
        this.selectedStudios = this.availableStudios.map((studio) => studio.value)
      }
      this.fetchHealthcareRequests()
    },
    async createPDF(data) {
      // Da sich das Datenmodell geändert hat,
      // passe deine Payload hier an deine neuen Felder an:
      const request = data
      const formData = {
        test: true,
        contract: {
          wellness: data.wellness,
          ContractID: request.contract.ContractID,
          startDate: request.contract.start,
          endDate: request.contract.end,
          amount: request.amount,
          packages: data.packages,
          membershipName: request.contract.title,
          studioId: request.studio,
          // Das hier sind nur Beispielwerte
          studioPopulated: {
            art: null,
            adresse: null,
            healtcareConfirmationNumber: null,
            logo: null,
            website: null,
          },
        },
        customerId: request.user,
        // Falls du Kundendetails hast, bitte hier anpassen.
        customerInfos: {
          healthcareName: "Testversicherung",
          additionalInsurance: "Testzusatzversicherung",
          insuranceNumber: "Testversicherungsnummer",
          firstName: "Vorname",
          lastName: "Nachname",
          street: "Strasse",
          zip: "PLZ",
          city: "Stadt",
          signature: null,
        },
      }

      try {
        this.$toast.success("PDF wird erstellt")
        await api.createHealthcarePDF(formData)
      } catch (e) {
        console.error(e)
        this.$toast.error("Fehler beim Herunterladen, bitte versuche es erneut")
      }
    },
    additionalPaymentInfos(trans) {
      if (!this.selectedRequest || !this.selectedRequest.contract || !this.selectedRequest.contract.yearCount || !trans || !trans.Value) return ""

      return " / " + this.selectedRequest.contract.yearCount + " = " + Number(trans.Value.value) / this.selectedRequest.contract.yearCount + " CHF"
    },
    /**
     * Beispiel-Funktion zum Freischalten / Payment updaten
     */
    async updateContractPayment() {
      if (this.selectedRequest?.amount > 0 && this.selectedRequest?._id && this.selectedRequest?.user) {
        try {
          await api.updateHealthcareRequest({
            amount: this.selectedRequest.amount,
            packages: this.selectedRequest.packages,
            wellness: this.selectedRequest.wellness,
            status: "accepted",
            title: this.selectedRequest.contract.title,
            requestId: this.selectedRequest._id,
          })
          this.$toast.success("Status aktualisiert")
          this.selectedRequest = null
          this.requests = []
          this.fetchHealthcareRequests()
        } catch (e) {
          console.error(e)
          this.$toast.error("Fehler beim Aktualisieren")
        }
      } else {
        this.$toast.error("Fülle alle Felder aus")
      }
    },

    /**
     * Server-Abfrage
     */
    async fetchHealthcareRequests() {
      this.loading = true
      try {
        const response = await api.fetchHealthcareRequestsAdmin(this.selectedStatus, this.selectedStudios)
        this.requests = Array.isArray(response.requests) ? response.requests : []

        // Gruppiere Studios mit offenen Anfragen
        const studioCounts = {}
        for (const request of response.openStuds || []) {
          const studioName = request && request.studio && request.studio.name ? request.studio.name : "Unbekanntes Studio"
          if (!studioCounts[studioName]) {
            studioCounts[studioName] = 0
          }
          studioCounts[studioName]++
        }

        // Konvertiere in ein Array für die Anzeige
        this.groupedOpenStudios = Object.entries(studioCounts).map(([name, count]) => ({
          name,
          count,
        }))
      } catch (error) {
        console.error(error)
        this.$error("Fehler beim Laden der Anfragen")
      } finally {
        this.loading = false
      }
    },
    insertCost() {
      let price = 0
      for (let transaction of this.contractTransactions) {
        if (transaction.checked && transaction.Value) {
          let divider = 1

          if (this.selectedRequest.contract.yearCount) {
            divider = this.selectedRequest.contract.yearCount
          }

          price += Number(transaction.Value.value / divider)
        }
      }
      this.selectedRequest.amount = price
    },
  },
  computed: {
    ...mapState("studioStore", ["storeStudios"]),
    allStudiosSelected() {
      // Überprüft, ob alle verfügbaren Studios ausgewählt sind
      return this.availableStudios.length > 0 && this.selectedStudios.length === this.availableStudios.length
    },

    someStudiosSelected() {
      // Überprüft, ob einige, aber nicht alle Studios ausgewählt sind
      return this.selectedStudios.length > 0 && !this.allStudiosSelected
    },

    selectAllIcon() {
      // Gibt das passende Icon basierend auf der Auswahl zurück
      if (this.allStudiosSelected) return "mdi-close-box"
      if (this.someStudiosSelected) return "mdi-minus-box"
      return "mdi-checkbox-blank-outline"
    },
    requestsFiltered() {
      if (!Array.isArray(this.requests) || !this.requests.length) {
        return []
      }

      let filteredRequests = this.requests

      // Falls der Nutzer keine Studios ausgewählt hat, zeig alle
      if (this.searchFilter && this.searchFilter.trim()) {
        const query = this.searchFilter.trim().toLowerCase()
        return this.requests.filter((req) => {
          const user = req && req.user ? req.user : {}
          return [user.vorname, user.nachname, user.email].filter(Boolean).join(" ").toLowerCase().includes(query)
        })
      }

      return filteredRequests
    },

    /**
     * Liste aller verfügbaren Studios (IDs),
     * abgeleitet aus den Daten.
     */
    availableStudios() {
      if (!this.storeStudios) return []
      return this.storeStudios
        .filter((studio) => studio.code)
        .map((studio) => {
          return {
            text: studio.name,
            value: studio._id,
          }
        })
    },
    contractTransactions() {
      if (!this.selectedRequest || !this.selectedRequest.contract || !Array.isArray(this.selectedRequest.contract.transactions)) return []

      const contractTitle = this.selectedRequest.contract.title || ""
      if (
        contractTitle.includes("AHV") ||
        contractTitle.includes("Erwachsene") ||
        contractTitle.includes("PREMIUM") ||
        contractTitle.includes("Premium")
      ) {
        this.selectedRequest.wellness = true
      }
      this.selectedRequest.packages = ""
      let allTrans = []

      for (let transaction of this.selectedRequest.contract.transactions) {
        const transactionDetails =
          transaction && transaction.det && Array.isArray(transaction.det.AccountTransactionDetail) ? transaction.det.AccountTransactionDetail : []
        for (let detTransaction of transactionDetails) {
          const description = detTransaction.Description || ""
          if (description.includes("Einschreib") || description.includes("Aktivierungs")) {
            continue
          } else {
            if (description.includes("Wellness") || description.includes("Spa")) {
              this.selectedRequest.wellness = true
            }

            if (typeof detTransaction.checked !== "boolean") this.$set(detTransaction, "checked", true)
            allTrans.push(detTransaction)
          }
        }
      }
      return allTrans
    },
  },
}
</script>

<style lang="scss" scoped>
.healthcare-shell {
  --surface: rgba(255, 255, 255, 0.035);
  --surface-strong: rgba(255, 255, 255, 0.055);
  --border: rgba(255, 255, 255, 0.1);
  --border-strong: rgba(255, 255, 255, 0.16);
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  --primary: #009640;
  --primary-soft: #58d388;
  max-width: 1480px;
  margin: 0 auto;
  padding: 24px;
  color: var(--text);
}

.healthcare-hero {
  position: relative;
  display: flex;
  min-height: 190px;
  align-items: center;
  justify-content: space-between;
  gap: 28px;
  padding: 34px 38px;
  overflow: hidden;
  border: 1px solid rgba(255, 255, 255, 0.11);
  border-radius: 24px;
  background: linear-gradient(120deg, rgba(0, 150, 64, 0.08), transparent 48%), rgba(255, 255, 255, 0.035);
  box-shadow: 0 24px 70px rgba(0, 0, 0, 0.2);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.healthcare-hero::after {
  position: absolute;
  right: 10%;
  bottom: -180px;
  width: 280px;
  height: 280px;
  border-radius: 50%;
  background: rgba(0, 150, 64, 0.1);
  content: "";
  filter: blur(22px);
  pointer-events: none;
}

.hero-content,
.hero-actions,
.surface-title-row,
.surface-header,
.requests-header,
.status-legend,
.status-legend span,
.status-pill,
.studio-label,
.request-topline,
.request-person,
.contract-heading,
.contract-dates,
.contract-dates span,
.request-footer,
.request-actions,
.created-date,
.section-heading,
.section-heading-copy,
.section-heading-between,
.inline-empty,
.dialog-actions {
  display: flex;
  align-items: center;
}

.hero-content {
  position: relative;
  z-index: 1;
  min-width: 0;
  gap: 20px;
}

.hero-mark,
.kpi-icon,
.person-avatar,
.empty-state-icon,
.dialog-mark,
.section-icon,
.transaction-icon {
  display: inline-grid;
  flex: 0 0 auto;
  place-items: center;
}

.hero-mark {
  width: 58px;
  height: 58px;
  border: 1px solid rgba(0, 150, 64, 0.28);
  border-radius: 17px;
  color: var(--primary-soft);
  background: rgba(0, 150, 64, 0.13);
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.05);
}

.hero-mark .v-icon {
  color: currentColor;
  font-size: 29px;
}

.hero-copy {
  min-width: 0;
}

.page-eyebrow,
.surface-eyebrow,
.dialog-eyebrow,
.section-heading > div > span {
  color: var(--primary-soft);
  font-size: 10px;
  font-weight: 800;
  line-height: 1.3;
  letter-spacing: 0.13em;
  text-transform: uppercase;
}

.hero-copy h1 {
  margin: 5px 0 7px;
  color: #ffffff;
  font-size: clamp(30px, 4vw, 44px);
  font-weight: 760;
  line-height: 1.08;
  letter-spacing: -0.035em;
}

.hero-copy p {
  max-width: 700px;
  margin: 0;
  color: #b8b8bc;
  font-size: 15px;
  line-height: 1.6;
}

.healthcare-hero > .status-pill {
  position: relative;
  z-index: 1;
  flex: 0 0 auto;
}

.kpi-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
  margin: 16px 0;
}

.kpi-card {
  display: flex;
  min-width: 0;
  min-height: 108px;
  align-items: center;
  gap: 14px;
  padding: 18px;
  border: 1px solid var(--border);
  border-radius: 19px !important;
  background: var(--surface) !important;
  box-shadow: 0 12px 30px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  transition: transform 160ms ease, border-color 160ms ease, background-color 160ms ease;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  border-radius: 13px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.065);
}

.kpi-icon .v-icon {
  color: currentColor;
  font-size: 20px;
}

.kpi-icon-pending {
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.12);
}

.kpi-icon-success {
  color: var(--primary-soft);
  background: rgba(0, 150, 64, 0.11);
}

.kpi-copy {
  display: grid;
  min-width: 0;
}

.kpi-copy > span {
  overflow: hidden;
  color: var(--text-soft);
  font-size: 11px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-copy strong {
  color: #ffffff;
  font-size: 27px;
  font-weight: 760;
  line-height: 1.15;
  letter-spacing: -0.03em;
}

.kpi-copy strong.kpi-status {
  overflow: hidden;
  font-size: clamp(15px, 1.35vw, 19px);
  line-height: 1.3;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.kpi-copy small {
  margin-top: 3px;
  overflow: hidden;
  color: var(--muted);
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.filter-surface,
.requests-surface {
  border: 1px solid var(--border);
  border-radius: 22px !important;
  background: rgba(255, 255, 255, 0.03) !important;
  box-shadow: 0 16px 44px rgba(0, 0, 0, 0.12);
  backdrop-filter: blur(18px);
  -webkit-backdrop-filter: blur(18px);
}

.filter-surface {
  margin-bottom: 16px;
  padding: 20px 22px 14px;
}

.surface-header,
.requests-header,
.section-heading-between,
.request-topline,
.request-footer {
  justify-content: space-between;
  gap: 18px;
}

.surface-title-row {
  gap: 11px;
  margin-top: 3px;
}

.surface-title-row h2,
.requests-header h2 {
  margin: 0;
  color: var(--text);
  font-size: 20px;
  font-weight: 740;
  letter-spacing: -0.02em;
}

.result-count {
  display: inline-flex;
  min-height: 25px;
  align-items: center;
  justify-content: center;
  padding: 3px 9px;
  border: 1px solid var(--border);
  border-radius: 999px;
  color: var(--text-soft);
  background: rgba(255, 255, 255, 0.055);
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
}

.filter-fields {
  margin-top: 10px;
}

.modern-field ::v-deep .v-input__slot,
.dialog-field ::v-deep .v-input__slot {
  min-height: 46px !important;
  border-radius: 13px !important;
  background: rgba(255, 255, 255, 0.035) !important;
}

.modern-field ::v-deep fieldset,
.dialog-field ::v-deep fieldset {
  border-color: rgba(255, 255, 255, 0.15) !important;
}

.modern-field ::v-deep .v-label,
.dialog-field ::v-deep .v-label {
  color: #ababaf !important;
}

.modern-field ::v-deep input,
.dialog-field ::v-deep input {
  color: var(--text) !important;
}

.modern-field.v-input--is-focused ::v-deep fieldset,
.dialog-field.v-input--is-focused ::v-deep fieldset {
  border-color: #009640 !important;
}

.modern-field.v-input--is-focused ::v-deep .v-icon,
.dialog-field.v-input--is-focused ::v-deep .v-icon {
  color: var(--primary-soft) !important;
}

.healthcare-shell ::v-deep .primary--text,
.confirmation-dialog ::v-deep .primary--text {
  color: #58d388 !important;
  caret-color: #58d388 !important;
}

.healthcare-shell ::v-deep .v-progress-circular.primary--text {
  color: #009640 !important;
}

.studio-select ::v-deep .v-chip {
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: #d4d4d8;
  background: rgba(255, 255, 255, 0.06);
}

.requests-surface {
  padding-top: 22px;
  overflow: hidden;
}

.requests-header {
  padding: 0 22px 17px;
}

.status-legend {
  gap: 14px;
  color: var(--muted);
  font-size: 10px;
}

.status-legend span {
  gap: 6px;
}

.legend-dot,
.status-dot {
  display: inline-block;
  flex: 0 0 auto;
  width: 7px;
  height: 7px;
  border-radius: 50%;
}

.legend-pending,
.status-pending .status-dot {
  background: #fbbf24;
}

.legend-accepted,
.status-accepted .status-dot {
  background: #58d388;
}

.status-pill {
  min-height: 28px;
  gap: 7px;
  padding: 4px 10px;
  border: 1px solid transparent;
  border-radius: 999px;
  font-size: 10px;
  font-weight: 750;
  line-height: 1;
  white-space: nowrap;
}

.status-pending {
  border-color: rgba(251, 191, 36, 0.2);
  color: #fcd34d;
  background: rgba(245, 158, 11, 0.1);
}

.status-accepted {
  border-color: rgba(0, 150, 64, 0.27);
  color: #66d691;
  background: rgba(0, 150, 64, 0.11);
}

.loading-state,
.empty-state {
  display: flex;
  min-height: 270px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  padding: 38px 20px;
  border-top: 1px solid var(--border);
  color: var(--muted);
  text-align: center;
}

.loading-state {
  gap: 8px;
}

.loading-state .v-progress-circular {
  margin-bottom: 10px;
}

.loading-state strong,
.empty-state strong {
  color: #e4e4e7;
  font-size: 14px;
}

.loading-state span,
.empty-state p {
  margin: 0;
  font-size: 11px;
}

.empty-state-icon {
  width: 54px;
  height: 54px;
  margin-bottom: 13px;
  border: 1px solid var(--border);
  border-radius: 16px;
  color: #b8b8bc;
  background: rgba(255, 255, 255, 0.055);
}

.empty-state-icon .v-icon {
  color: currentColor;
}

.empty-state p {
  margin-top: 5px;
}

.open-studios {
  width: min(100%, 620px);
  margin-top: 22px;
  padding-top: 18px;
  border-top: 1px solid rgba(255, 255, 255, 0.075);
}

.open-studios-heading {
  margin-bottom: 9px;
  color: #8f8f93;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.open-studio-list {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 7px;
}

.open-studio-pill {
  display: inline-flex;
  min-height: 30px;
  align-items: center;
  gap: 8px;
  padding: 4px 9px;
  border: 1px solid rgba(251, 191, 36, 0.16);
  border-radius: 999px;
  color: #d4d4d8;
  background: rgba(245, 158, 11, 0.06);
  font-size: 10px;
}

.open-studio-pill strong {
  color: #fcd34d;
  font-size: 10px;
}

.request-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 12px;
  padding: 14px;
  border-top: 1px solid var(--border);
}

.request-card {
  display: flex;
  min-width: 0;
  min-height: 310px;
  flex-direction: column;
  padding: 16px;
  border: 1px solid var(--border);
  border-radius: 18px;
  background: rgba(255, 255, 255, 0.04);
  transition: transform 150ms ease, border-color 150ms ease, background-color 150ms ease;
}

.request-topline {
  min-width: 0;
}

.studio-label {
  min-width: 0;
  gap: 5px;
  overflow: hidden;
  color: var(--muted);
  font-size: 10px;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.studio-label .v-icon {
  color: #8f8f93;
}

.request-person {
  min-width: 0;
  gap: 11px;
  margin: 18px 0 15px;
}

.person-avatar {
  width: 43px;
  height: 43px;
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 13px;
  color: #e4e4e7;
  background: rgba(255, 255, 255, 0.065);
  font-size: 11px;
  font-weight: 800;
}

.request-person > div {
  display: grid;
  min-width: 0;
}

.request-person strong {
  overflow: hidden;
  color: var(--text);
  font-size: 15px;
  font-weight: 740;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.request-person span {
  margin-top: 2px;
  overflow: hidden;
  color: var(--muted);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contract-box {
  padding: 13px;
  border: 1px solid rgba(255, 255, 255, 0.075);
  border-radius: 14px;
  background: rgba(0, 0, 0, 0.13);
}

.contract-heading {
  gap: 6px;
  color: #8f8f93;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.contract-heading .v-icon {
  color: #8f8f93;
}

.contract-box > strong {
  display: block;
  margin-top: 7px;
  overflow: hidden;
  color: #dddddf;
  font-size: 12px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contract-dates {
  justify-content: space-between;
  gap: 7px;
  margin-top: 11px;
  padding-top: 9px;
  border-top: 1px solid rgba(255, 255, 255, 0.065);
  color: #a3a3a8;
}

.contract-dates span {
  min-width: 0;
  gap: 4px;
  overflow: hidden;
  font-size: 10px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.contract-dates .v-icon {
  color: #8f8f93;
}

.request-footer {
  margin-top: auto;
  padding-top: 14px;
}

.created-date {
  gap: 5px;
  color: var(--muted);
  font-size: 10px;
}

.created-date .v-icon {
  color: #8f8f93;
}

.request-actions {
  flex: 0 0 auto;
  gap: 7px;
}

.touch-action {
  width: 44px !important;
  min-width: 44px !important;
  height: 44px !important;
  min-height: 44px !important;
  border: 1px solid var(--border);
  border-radius: 12px !important;
  color: var(--text-soft) !important;
  background: rgba(255, 255, 255, 0.055) !important;
}

.delete-action {
  border-color: rgba(251, 113, 133, 0.18);
  color: #fda4af !important;
  background: rgba(244, 63, 94, 0.075) !important;
}

.unlock-action,
.take-over-action,
.test-pdf-action {
  min-height: 44px !important;
  border-radius: 12px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.unlock-action {
  border-color: rgba(245, 158, 11, 0.27) !important;
  color: #fcd34d !important;
  background: rgba(245, 158, 11, 0.055);
}

.primary-action,
.secondary-action {
  min-height: 44px !important;
  border-radius: 12px !important;
  font-weight: 700;
  letter-spacing: 0;
  text-transform: none;
}

.primary-action:not(.v-btn--disabled) {
  color: #ffffff !important;
  background-color: #009640 !important;
  box-shadow: 0 9px 22px rgba(0, 150, 64, 0.15);
}

.download-action {
  padding: 0 13px !important;
}

.confirmation-dialog {
  --text: #f4f4f5;
  --text-soft: #d4d4d8;
  --muted: #a3a3a8;
  --border: rgba(255, 255, 255, 0.1);
  --primary-soft: #58d388;
  color: var(--text);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 24px !important;
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.018), transparent 38%), rgba(28, 28, 29, 0.985) !important;
  box-shadow: 0 30px 100px rgba(0, 0, 0, 0.54);
}

.dialog-header {
  display: flex;
  flex-wrap: nowrap;
  gap: 12px;
  min-height: 94px;
  padding: 18px 22px !important;
  background: linear-gradient(115deg, rgba(0, 150, 64, 0.055), transparent 50%), rgba(255, 255, 255, 0.018);
  white-space: normal;
}

.dialog-mark {
  width: 48px;
  height: 48px;
  border: 1px solid rgba(0, 150, 64, 0.26);
  border-radius: 14px;
  color: #58d388;
  background: rgba(0, 150, 64, 0.12);
}

.dialog-mark .v-icon {
  color: currentColor;
}

.dialog-title-copy {
  min-width: 0;
}

.dialog-eyebrow {
  color: #58d388;
}

.dialog-title-copy h2 {
  margin: 2px 0;
  overflow: hidden;
  color: #ffffff;
  font-size: 20px;
  font-weight: 750;
  line-height: 1.25;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-title-copy p {
  margin: 0;
  overflow: hidden;
  color: var(--muted);
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.dialog-close:hover {
  color: #ffffff !important;
  background: rgba(255, 255, 255, 0.09) !important;
}

.dialog-divider {
  border-color: rgba(255, 255, 255, 0.085) !important;
}

.dialog-scroll {
  max-height: 74vh;
  padding: 18px 20px 22px !important;
}

.dialog-section {
  padding: 16px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.035);
}

.dialog-section + .dialog-section {
  margin-top: 12px;
}

.section-heading {
  gap: 10px;
  margin-bottom: 14px;
}

.section-heading-copy {
  min-width: 0;
  gap: 10px;
}

.section-icon {
  width: 34px;
  height: 34px;
  border-radius: 10px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.06);
}

.section-icon .v-icon {
  color: currentColor;
}

.section-heading > div:not(.section-heading-copy),
.section-heading-copy > div {
  display: grid;
  min-width: 0;
}

.section-heading > div > span,
.section-heading-copy > div > span {
  color: #8f8f93;
  font-size: 8px;
}

.section-heading h3 {
  margin: 1px 0 0;
  color: #eeeeef;
  font-size: 14px;
  font-weight: 730;
}

.detail-list {
  margin: 0;
}

.detail-row {
  display: grid;
  grid-template-columns: minmax(100px, 0.65fr) minmax(0, 1.35fr);
  gap: 12px;
  padding: 8px 0;
  border-top: 1px solid rgba(255, 255, 255, 0.065);
  font-size: 12px;
  line-height: 1.45;
}

.detail-row:first-child {
  padding-top: 0;
  border-top: 0;
}

.detail-row:last-child {
  padding-bottom: 0;
}

.detail-row dt {
  color: #929296;
}

.detail-row dd {
  min-width: 0;
  margin: 0;
  overflow-wrap: anywhere;
  color: #dddddf;
  font-weight: 600;
}

.transaction-list {
  display: grid;
  gap: 8px;
}

.transaction-row {
  display: grid;
  min-width: 0;
  grid-template-columns: 38px minmax(0, 1fr) 44px;
  align-items: center;
  gap: 10px;
  padding: 10px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 13px;
  background: rgba(0, 0, 0, 0.13);
  cursor: pointer;
}

.transaction-icon {
  width: 36px;
  height: 36px;
  border-radius: 11px;
  color: #c5c5c8;
  background: rgba(255, 255, 255, 0.06);
}

.transaction-icon .v-icon {
  color: currentColor;
}

.transaction-copy {
  display: grid;
  min-width: 0;
}

.transaction-copy strong {
  overflow: hidden;
  color: #dddddf;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.transaction-copy > span {
  margin-top: 2px;
  color: #b8b8bc;
  font-size: 10px;
}

.transaction-copy em {
  color: #a3a3a8;
  font-style: normal;
}

.transaction-copy .is-negative,
.transaction-copy .is-negative em {
  color: #fda4af;
}

.transaction-check {
  min-width: 44px;
  margin: 0 !important;
  padding: 0 !important;
}

.transaction-check ::v-deep .v-input--selection-controls__input {
  margin: auto;
}

.inline-empty {
  min-height: 48px;
  gap: 8px;
  padding: 10px 12px;
  border: 1px dashed rgba(255, 255, 255, 0.11);
  border-radius: 12px;
  color: #929296;
  background: rgba(255, 255, 255, 0.025);
  font-size: 11px;
}

.take-over-action {
  width: 100%;
  margin-top: 11px;
  border-color: rgba(0, 150, 64, 0.25) !important;
  color: #66d691 !important;
}

.dialog-field + .dialog-field {
  margin-top: 10px !important;
}

.wellness-option {
  min-height: 52px;
  margin-top: 10px;
  padding: 4px 11px;
  border: 1px solid rgba(255, 255, 255, 0.09);
  border-radius: 13px;
  background: rgba(255, 255, 255, 0.035);
}

.wellness-option ::v-deep .v-input--selection-controls {
  min-height: 44px;
  margin: 0;
}

.dialog-actions {
  min-height: 72px;
  gap: 8px;
  padding: 12px 20px !important;
  background: rgba(20, 20, 21, 0.92);
}

.secondary-action {
  color: #d4d4d8 !important;
}

.test-pdf-action {
  border-color: rgba(245, 158, 11, 0.27) !important;
  color: #fcd34d !important;
}

.touch-action:focus-visible,
.unlock-action:focus-visible,
.primary-action:focus-visible,
.secondary-action:focus-visible,
.test-pdf-action:focus-visible,
.take-over-action:focus-visible,
.transaction-row:focus-within {
  outline: 2px solid #58d388;
  outline-offset: 2px;
}

@media (hover: hover) and (pointer: fine) {
  .kpi-card:hover {
    transform: translateY(-2px);
    border-color: var(--border-strong);
    background: var(--surface-strong) !important;
  }

  .request-card:hover {
    transform: translateY(-1px);
    border-color: var(--border-strong);
    background: rgba(255, 255, 255, 0.055);
  }

  .unlock-action:hover {
    border-color: rgba(245, 158, 11, 0.4) !important;
    background: rgba(245, 158, 11, 0.1);
  }

  .delete-action:hover {
    border-color: rgba(251, 113, 133, 0.3);
    background: rgba(244, 63, 94, 0.12) !important;
  }
}

@media (max-width: 1263px) {
  .healthcare-shell {
    padding: 18px;
  }

  .kpi-grid,
  .request-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 959px) {
  .healthcare-hero {
    min-height: 172px;
    padding: 28px;
  }
}

@media (max-width: 599px) {
  .healthcare-shell {
    padding: 12px;
  }

  .healthcare-hero {
    align-items: flex-start;
    min-height: auto;
    flex-direction: column;
    gap: 14px;
    padding: 21px 18px 18px;
    border-radius: 20px;
  }

  .hero-content {
    align-items: flex-start;
    gap: 12px;
  }

  .hero-mark {
    width: 46px;
    height: 46px;
    border-radius: 14px;
  }

  .hero-mark .v-icon {
    font-size: 23px;
  }

  .page-eyebrow {
    font-size: 8px;
  }

  .hero-copy h1 {
    margin-top: 3px;
    font-size: 26px;
  }

  .hero-copy p {
    margin-top: 8px;
    font-size: 11px;
    line-height: 1.55;
  }

  .healthcare-hero > .status-pill {
    position: static;
    align-self: flex-start;
  }

  .kpi-grid {
    gap: 7px;
    margin: 10px 0;
  }

  .kpi-card {
    display: grid;
    min-height: 110px;
    align-content: start;
    gap: 7px;
    padding: 11px 10px;
    border-radius: 15px !important;
  }

  .kpi-icon {
    width: 32px;
    height: 32px;
    border-radius: 10px;
  }

  .kpi-copy > span {
    font-size: 9px;
    white-space: normal;
  }

  .kpi-copy strong {
    font-size: 22px;
  }

  .kpi-copy small {
    display: none;
  }

  .filter-surface {
    margin-bottom: 10px;
    padding: 17px 14px 10px;
    border-radius: 18px !important;
  }

  .surface-title-row {
    align-items: flex-start;
    flex-direction: column;
    gap: 5px;
  }

  .surface-title-row h2,
  .requests-header h2 {
    font-size: 18px;
  }

  .requests-surface {
    padding-top: 18px;
    border-radius: 18px !important;
  }

  .requests-header {
    padding: 0 15px 14px;
  }

  .status-legend {
    display: none;
  }

  .request-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 9px;
    padding: 10px;
  }

  .request-card {
    min-height: 300px;
    padding: 14px;
  }

  .confirmation-dialog {
    border: 0;
    border-radius: 0 !important;
  }

  .dialog-header {
    min-height: 86px;
    padding: 14px !important;
  }

  .dialog-mark {
    width: 42px;
    height: 42px;
    border-radius: 12px;
  }

  .dialog-title-copy {
    max-width: calc(100% - 108px);
  }

  .dialog-title-copy h2 {
    font-size: 17px;
  }

  .dialog-scroll {
    max-height: none;
    padding: 14px !important;
  }

  .dialog-section {
    padding: 14px;
    border-radius: 14px;
  }

  .detail-row {
    grid-template-columns: minmax(88px, 0.6fr) minmax(0, 1.4fr);
    gap: 8px;
  }

  .dialog-actions {
    flex-wrap: wrap;
    padding: 11px 14px calc(11px + env(safe-area-inset-bottom)) !important;
  }

  .dialog-actions > .spacer {
    display: none;
  }

  .secondary-action {
    margin-right: auto;
  }

  .test-pdf-action,
  .dialog-actions .primary-action {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
  }
}

@media (max-width: 479px) {
  .hero-copy p {
    display: none;
  }

  .request-footer {
    align-items: flex-start;
    flex-direction: column;
  }

  .request-actions,
  .unlock-action,
  .download-action {
    width: 100%;
  }

  .unlock-action {
    flex: 1;
  }

  .detail-row {
    grid-template-columns: minmax(0, 1fr);
    gap: 3px;
  }

  .dialog-actions {
    flex-direction: column-reverse;
  }

  .secondary-action,
  .test-pdf-action,
  .dialog-actions .primary-action {
    width: 100%;
  }
}
</style>
